import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import { AccountTree, Warning, ExpandMore } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ThemeModeContext } from '../index';
import diagramService from '../services/diagramService';
import logger from '../utils/logger';
import BlockSelectionDialog from '../components/BlockSelectionDialog';
import WorkspaceLayout from '../components/WorkspaceLayout';
import OptionsPanel, { useCollapsiblePanel } from '../components/OptionsPanel';
import SegmentedControl from '../components/SegmentedControl';
import VirtualizedCheckList from '../components/VirtualizedCheckList';
import DiagramPanel from '../components/diagram/DiagramPanel';
import DiagramOptions from '../components/diagram/DiagramOptions';

const ALL_PLATFORMS = ['Windows', 'macOS', 'iOS', 'Android'];
const ICON_LIMIT = 500;

const Online = () => {
  const { isAuthenticated } = useAuth();
  const { actualMode } = React.useContext(ThemeModeContext);

  const [panelCollapsed, togglePanel] = useCollapsiblePanel('onlineOptionsCollapsed');

  const [formData, setFormData] = useState({
    type: 'Applications',
    groupBy: 'Name',
    operatingSystem: ALL_PLATFORMS,
    direction: 'TB',
    layout: 'default',
    displayIcons: true,
    policyType: [],
    applicationType: [],
    appendVersion: false,
    excludeSupersededApps: false,
    theme: actualMode === 'dark' ? 'dark' : 'default'
  });

  const [mermaidCode, setMermaidCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [availablePolicyTypes, setAvailablePolicyTypes] = useState([]);
  const [availableApplicationTypes, setAvailableApplicationTypes] = useState([]);
  const [availableScopeTags, setAvailableScopeTags] = useState([]);
  const [selectedScopeTags, setSelectedScopeTags] = useState([]);
  const [diagramMetadata, setDiagramMetadata] = useState(null);
  const [appList, setAppList] = useState([]);
  const [profileList, setProfileList] = useState({ deviceConfigurations: [], configurationPolicies: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [appCount, setAppCount] = useState(0);
  const [iconsManuallyToggled, setIconsManuallyToggled] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);

  // Guards for the selection list. React StrictMode double-invokes effects in
  // development, and an effect re-run for any reason would otherwise let a
  // stale response overwrite a selection the user has already made.
  const loadTokenRef = useRef(0);
  const hasPromptedRef = useRef(false);

  // Profile types present in the scope-tag-filtered profiles.
  const scopeTagFilteredPolicyTypes = useMemo(() => {
    if (selectedScopeTags.length === 0) return availablePolicyTypes;

    const matches = (p) => (p.roleScopeTagIds || ['0']).some((tag) => selectedScopeTags.includes(tag));
    const matchingTypeNames = new Set([
      ...profileList.deviceConfigurations.filter(matches).map((p) => p.profileType),
      ...profileList.configurationPolicies.filter(matches).map((p) => p.profileType)
    ]);

    return availablePolicyTypes.filter((pt) => matchingTypeNames.has(pt.displayName));
  }, [availablePolicyTypes, profileList, selectedScopeTags]);

  const filteredPolicyTypes = useMemo(() => {
    if (!formData.operatingSystem || formData.operatingSystem.length === 0) {
      return scopeTagFilteredPolicyTypes;
    }
    return scopeTagFilteredPolicyTypes.filter((pt) => formData.operatingSystem.includes(pt.os));
  }, [scopeTagFilteredPolicyTypes, formData.operatingSystem]);

  // Drop selected policy types that the OS filter just removed.
  useEffect(() => {
    if (formData.policyType.length === 0) return;
    const validIds = filteredPolicyTypes.map((pt) => pt.id);
    const stillValid = formData.policyType.filter((id) => validIds.includes(id));
    if (stillValid.length !== formData.policyType.length) {
      setFormData((prev) => ({ ...prev, policyType: stillValid }));
    }
  }, [filteredPolicyTypes, formData.policyType]);

  // Follow the site theme.
  useEffect(() => {
    setFormData((prev) => ({ ...prev, theme: actualMode === 'dark' ? 'dark' : 'default' }));
  }, [actualMode]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const loadScopeTags = async () => {
      try {
        const data = await diagramService.getScopeTags();
        setAvailableScopeTags(data.scopeTags || []);
      } catch (err) {
        // 403 is expected without DeviceManagementRBAC.Read.All - hide the filter.
        logger.log('[Online] Scope tags not available');
        setAvailableScopeTags([]);
      }
    };
    loadScopeTags();
  }, [isAuthenticated]);

  const loadSelectionList = useCallback(async () => {
    const token = ++loadTokenRef.current;
    const isStale = () => token !== loadTokenRef.current;

    setListLoading(true);
    try {
      if (formData.type === 'Applications') {
        const data = await diagramService.listApplications(true);
        // A newer load started while this one was in flight - drop this result
        // rather than clobbering whatever the user has since selected.
        if (isStale()) return;

        const applications = data.applications || [];
        logger.log('[Online] Loaded applications:', applications.length);

        setAppList(applications);
        setAppCount(applications.length);

        const uniqueAppTypes = [...new Set(applications.map((a) => a.appType).filter(Boolean))].sort();
        setAvailableApplicationTypes(uniqueAppTypes.map((t) => ({ id: t, displayName: t })));

        // Prompt once per dataset. Re-opening the dialog after someone has
        // already chosen a block is the bug this guard exists to prevent.
        if (applications.length > ICON_LIMIT && !hasPromptedRef.current) {
          hasPromptedRef.current = true;
          setBlockDialogOpen(true);
          setFormData((prev) => (prev.displayIcons ? { ...prev, displayIcons: false } : prev));
          setSuccess(
            `Loaded ${applications.length} applications. Select a block to keep rendering manageable.`
          );
        }
      } else if (formData.type === 'Profiles') {
        const data = await diagramService.listProfiles(true);
        if (isStale()) return;
        const deviceConfigs = data.deviceConfigurations || [];
        const configPolicies = data.configurationPolicies || [];
        logger.log('[Online] Loaded profiles:', deviceConfigs.length, '+', configPolicies.length);

        setProfileList({ deviceConfigurations: deviceConfigs, configurationPolicies: configPolicies });
        setAppCount(0);

        const profileTypes = [];
        const addType = (p, idPrefix, category) => {
          const t = p.profileType;
          if (t && !profileTypes.find((pt) => pt.displayName === t)) {
            profileTypes.push({ id: `${idPrefix}${t}`, displayName: t, category, os: p.os || 'Windows' });
          }
        };
        deviceConfigs.forEach((p) => addType(p, 'dc_', 'deviceConfiguration'));
        configPolicies.forEach((p) => addType(p, 'sc_', 'settingsCatalog'));

        setAvailablePolicyTypes(profileTypes.sort((a, b) => a.displayName.localeCompare(b.displayName)));
      }
    } catch (err) {
      if (isStale()) return;
      logger.error('Failed to load selection list:', err);
      setError(`Failed to load ${formData.type.toLowerCase()}: ${err.message}`);
    } finally {
      if (!isStale()) setListLoading(false);
    }
  }, [formData.type]);

  const loadedForRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Only treat this as a new dataset when the type actually changed.
    // Re-running the effect for any other reason must not discard a selection.
    const datasetKey = formData.type;
    if (loadedForRef.current !== datasetKey) {
      logger.log('[Online][selection] new dataset', datasetKey, '- clearing selection');
      loadedForRef.current = datasetKey;
      setSelectedIds([]);
      setIconsManuallyToggled(false);
      hasPromptedRef.current = false;
    }

    loadSelectionList();
  }, [isAuthenticated, loadSelectionList, formData.type]);

  // Scope tag changes invalidate the current selection - but only a real
  // change, not the initial mount.
  const prevScopeTagsRef = useRef(selectedScopeTags);
  useEffect(() => {
    if (prevScopeTagsRef.current === selectedScopeTags) return;
    prevScopeTagsRef.current = selectedScopeTags;
    logger.log('[Online][selection] cleared by scope-tag change');
    setSelectedIds([]);
  }, [selectedScopeTags]);

  useEffect(() => {
    setIconsManuallyToggled(false);
  }, [selectedIds.length]);

  // Icons are expensive; turn them off automatically past the limit.
  useEffect(() => {
    if (formData.type !== 'Applications' || iconsManuallyToggled) return;

    const effectiveCount = selectedIds.length > 0 ? selectedIds.length : appCount;

    if (effectiveCount > ICON_LIMIT && formData.displayIcons) {
      setFormData((prev) => ({ ...prev, displayIcons: false }));
    } else if (effectiveCount > 0 && effectiveCount < ICON_LIMIT && !formData.displayIcons && selectedIds.length > 0) {
      // Strictly below the limit, so picking a full block never silently turns
      // icons back on: a block is exactly ICON_LIMIT apps, and the dialog only
      // appeared because icons had just been disabled for being too many.
      setFormData((prev) => ({ ...prev, displayIcons: true }));
    }
  }, [selectedIds.length, appCount, formData.type, formData.displayIcons, iconsManuallyToggled]);

  const handleBlockSelect = (ids, blockInfo) => {
    logger.log('[Online][selection] block chosen:', blockInfo.label, ids.length, 'ids');
    setSelectedIds(ids);
    setSelectedBlock(blockInfo);
    setSuccess(`Selected ${blockInfo.label}: ${blockInfo.count} applications (${blockInfo.range})`);
  };

  const handleRenderAll = () => {
    logger.log('[Online][selection] cleared by "select all"');
    setSelectedIds([]);
    setSelectedBlock(null);
    setSuccess(`Will render all ${appCount} applications. This may take a while.`);
  };

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  // --- Derived lists -----------------------------------------------------

  const matchesFilter = useCallback(
    (name) => !filterText || (name || '').toLowerCase().includes(filterText.toLowerCase()),
    [filterText]
  );

  const visibleApps = useMemo(() => {
    const byText = appList.filter((a) => matchesFilter(a.displayName));
    if (formData.applicationType.length === 0) return byText;
    return byText.filter((a) => formData.applicationType.includes(a.appType));
  }, [appList, matchesFilter, formData.applicationType]);

  const { visibleDeviceConfigs, visibleConfigPolicies } = useMemo(() => {
    const byScopeTag = (p) =>
      selectedScopeTags.length === 0 ||
      (p.roleScopeTagIds || ['0']).some((tag) => selectedScopeTags.includes(tag));

    const selectedTypeNames = formData.policyType
      .map((id) => availablePolicyTypes.find((t) => t.id === id))
      .filter(Boolean);

    const byType = (p, category) =>
      selectedTypeNames.length === 0 ||
      selectedTypeNames.some((t) => t.category === category && t.displayName === p.profileType);

    return {
      visibleDeviceConfigs: profileList.deviceConfigurations.filter(
        (p) => matchesFilter(p.displayName) && byScopeTag(p) && byType(p, 'deviceConfiguration')
      ),
      visibleConfigPolicies: profileList.configurationPolicies.filter(
        (p) => matchesFilter(p.displayName) && byScopeTag(p) && byType(p, 'settingsCatalog')
      )
    };
  }, [profileList, matchesFilter, selectedScopeTags, formData.policyType, availablePolicyTypes]);

  // --- Handlers ----------------------------------------------------------

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updates = { [field]: value };

      // Switching between Applications and Profiles resets every dependent filter.
      if (field === 'type') {
        updates.groupBy = 'Name';
        updates.operatingSystem = ALL_PLATFORMS;
        updates.direction = 'TB';
        updates.displayIcons = true;
        updates.policyType = [];
        updates.applicationType = [];
        updates.appendVersion = false;
        updates.excludeSupersededApps = false;
        updates.layout = 'default';
      }

      return { ...prev, ...updates };
    });

    if (field === 'type') {
      setSelectedScopeTags([]);
      setSelectedIds([]);
      setSelectedBlock(null);
      setFilterText('');
    }
  };

  const handleMultiSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const generateDiagram = async () => {
    if (!isAuthenticated) {
      setError('Please authenticate with Microsoft Graph first');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const baseParams = { ...formData };
      if (formData.type !== 'Applications') {
        delete baseParams.appendVersion;
        delete baseParams.excludeSupersededApps;
        delete baseParams.displayIcons;
      }

      if (selectedScopeTags.length > 0) {
        baseParams.scopeTagIds = selectedScopeTags;
      }

      // The backend expects profile type display names, not our internal ids.
      if (baseParams.policyType?.length > 0) {
        baseParams.policyType = baseParams.policyType.map((id) => {
          const typeObj = availablePolicyTypes.find((t) => t.id === id);
          return typeObj ? typeObj.displayName : id;
        });
      }

      logger.log('[Online][selection] generating with', selectedIds.length, 'selected ids');

      // Only the ids go over the wire. The server fetches from Graph and
      // filters before doing any per-object work, which avoids re-uploading
      // every selected application (icons included) just to narrow the set.
      const response = await diagramService.generateDiagram({
        ...baseParams,
        selectedIds
      });

      const code = response.mermaidCode || '';

      // The generator signals failures through sentinels in the returned code.
      if (/MODULE_NOT_FOUND/.test(code)) {
        setError('IntuneMermaid module not found on server. Please contact support.');
      } else if (/OFFLINE_UNSUPPORTED/.test(code)) {
        setError('Offline subset not supported by current IntuneMermaid version.');
      } else if (/OFFLINE_DATAFILE_MISSING/.test(code)) {
        setError('Server reported missing offline data file. Try again or contact support.');
      } else {
        setMermaidCode(code);
        setDiagramMetadata(response.metadata);
        setSuccess(response.warning || 'Diagram generated successfully!');
      }
    } catch (err) {
      logger.error('Failed to generate diagram:', err);
      let msg = err.message || 'Failed to generate diagram';
      if (err.payload?.diagnostics) {
        const { hasStoredAccessToken, sessionKeys = [] } = err.payload.diagnostics;
        msg += ` (sessionAuth=${hasStoredAccessToken}; sessionKeys=${sessionKeys.join(',')})`;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Shown on the collapsed rail so hiding the panel never hides the setup.
  const panelSummary = useMemo(() => {
    const items = [
      { label: 'Type', value: formData.type },
      { label: 'Group by', value: formData.groupBy }
    ];
    if (formData.operatingSystem.length && formData.operatingSystem.length < ALL_PLATFORMS.length) {
      items.push({ label: 'Platforms', value: formData.operatingSystem.join(', ') });
    }
    if (selectedIds.length > 0) {
      items.push({ label: 'Selected', value: `${selectedIds.length} selected` });
    }
    return items;
  }, [formData.type, formData.groupBy, formData.operatingSystem, selectedIds.length]);

  const autoDisabledIcons =
    !iconsManuallyToggled &&
    ((selectedIds.length === 0 && appCount > ICON_LIMIT) || selectedIds.length > ICON_LIMIT);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" icon={<Warning />}>
          <Typography variant="h6">Authentication Required</Typography>
          <Typography variant="body2">
            Please connect to Microsoft Graph using the button in the top-right corner to generate live
            diagrams.
          </Typography>
        </Alert>
      </Container>
    );
  }

  const optionsContent = (
    <>
      <SegmentedControl
        label="Type"
        value={formData.type}
        onChange={(value) => handleInputChange('type', value)}
        options={[
          { value: 'Applications', label: 'Applications' },
          { value: 'Profiles', label: 'Profiles' }
        ]}
      />

      <SegmentedControl
        label="Group by"
        value={formData.groupBy}
        onChange={(value) => handleInputChange('groupBy', value)}
        options={[
          { value: 'Name', label: 'Name' },
          { value: 'Assignments', label: 'Assignments' }
        ]}
      />

      <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
        <InputLabel>Operating Systems</InputLabel>
        <Select
          multiple
          value={formData.operatingSystem}
          label="Operating Systems"
          onChange={(e) => handleMultiSelectChange('operatingSystem', e.target.value)}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {ALL_PLATFORMS.map((os) => (
            <MenuItem key={os} value={os}>
              {os}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Only available with DeviceManagementRBAC.Read.All. */}
      {availableScopeTags.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Scope Tags</InputLabel>
          <Select
            multiple
            value={selectedScopeTags}
            label="Scope Tags"
            onChange={(e) => setSelectedScopeTags(e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const tag = availableScopeTags.find((t) => t.id === value);
                  return <Chip key={value} label={tag?.displayName || value} size="small" />;
                })}
              </Box>
            )}
          >
            {availableScopeTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {formData.type === 'Applications' && availableApplicationTypes.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Application Types</InputLabel>
          <Select
            multiple
            value={formData.applicationType}
            label="Application Types"
            onChange={(e) => handleMultiSelectChange('applicationType', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {availableApplicationTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {formData.type === 'Profiles' && filteredPolicyTypes.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Profile Types</InputLabel>
          <Select
            multiple
            value={formData.policyType}
            label="Profile Types"
            onChange={(e) => handleMultiSelectChange('policyType', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const typeObj = filteredPolicyTypes.find((t) => t.id === value);
                  return <Chip key={value} label={typeObj?.displayName || value} size="small" />;
                })}
              </Box>
            )}
          >
            {filteredPolicyTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Selection list */}
      {/* Styled to read as another outlined field rather than an elevated
          Paper: same 40px height, border, radius and padding as the Selects
          around it, so the panel is one consistent column of controls. */}
      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={false}
        sx={{
          mb: 1.25,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          '&:before': { display: 'none' },
          '&:first-of-type, &:last-of-type': { borderRadius: 1 },
          '& .MuiAccordionSummary-root': {
            minHeight: 40,
            px: 1.75,
            '&.Mui-expanded': { minHeight: 40 }
          },
          '& .MuiAccordionSummary-content': {
            my: 0,
            '&.Mui-expanded': { my: 0 }
          },
          '& .MuiAccordionDetails-root': { px: 1.75, pt: 0, pb: 1.5 }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">
              Select {formData.type}
            </Typography>
            {selectedIds.length > 0 && (
              <Chip label={selectedIds.length} size="small" color="primary" sx={{ mr: 1 }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {formData.type === 'Applications' && appCount > ICON_LIMIT && (
            <Alert
              severity="info"
              sx={{ mb: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => setBlockDialogOpen(true)}>
                  Select Block
                </Button>
              }
            >
              <Typography variant="body2">
                {appCount.toLocaleString()} apps detected.
                {selectedBlock
                  ? ` Currently showing ${selectedBlock.label} (${selectedBlock.count} apps).`
                  : ' Select a block for better performance.'}
              </Typography>
            </Alert>
          )}

          <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              label="Filter"
              placeholder="Search by name..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setFilterText('');
                loadSelectionList();
              }}
              sx={{ mt: 0.5 }}
            >
              {listLoading ? 'Reloading...' : 'Reload'}
            </Button>
          </Box>

          <VirtualizedCheckList
            loading={listLoading}
            selectedIds={selectedIds}
            onToggle={toggleSelected}
            sections={
              formData.type === 'Applications'
                ? [{ items: visibleApps }]
                : [
                    { title: 'Device Configurations', items: visibleDeviceConfigs },
                    { title: 'Configuration Policies', items: visibleConfigPolicies }
                  ]
            }
          />

          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip size="small" label={`Selected: ${selectedIds.length}`} />
            {selectedIds.length > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedIds([]);
                  setSelectedBlock(null);
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {formData.type === 'Applications' && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Advanced Options</InputLabel>
          <Select
            multiple
            value={[
              formData.displayIcons && 'Display Icons',
              formData.appendVersion && 'Append Version',
              formData.excludeSupersededApps && 'Exclude Superseded Apps'
            ].filter(Boolean)}
            label="Advanced Options"
            onChange={(e) => {
              const selected = e.target.value;
              const nextDisplayIcons = selected.includes('Display Icons');
              if (nextDisplayIcons !== formData.displayIcons) {
                setIconsManuallyToggled(true);
              }
              setFormData((prev) => ({
                ...prev,
                displayIcons: nextDisplayIcons,
                appendVersion: selected.includes('Append Version'),
                excludeSupersededApps: selected.includes('Exclude Superseded Apps')
              }));
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            <MenuItem value="Display Icons">Display Icons</MenuItem>
            <MenuItem value="Append Version">Append Version</MenuItem>
            <MenuItem value="Exclude Superseded Apps">Exclude Superseded Apps</MenuItem>
          </Select>
          {autoDisabledIcons && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Warning sx={{ fontSize: '1rem' }} />
              Icons auto-disabled for {ICON_LIMIT}+ entries
            </Typography>
          )}
        </FormControl>
      )}

      <DiagramOptions formData={formData} onChange={handleInputChange} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {diagramMetadata && (
        <Paper sx={{ p: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Generation Info
          </Typography>
          {diagramMetadata.objectCount !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {diagramMetadata.type}: {diagramMetadata.objectCount}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Execution Time:{' '}
            {diagramMetadata.executionTime
              ? `${(parseFloat(String(diagramMetadata.executionTime).replace('ms', '')) / 1000).toFixed(2)}s`
              : 'n/a'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated: {new Date(diagramMetadata.timestamp).toLocaleString()}
          </Typography>
        </Paper>
      )}
    </>
  );

  const generateButton = (
    <Button
      variant="contained"
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AccountTree />}
      onClick={generateDiagram}
      disabled={loading}
      fullWidth
    >
      {loading
        ? 'Generating...'
        : selectedIds.length > 0
          ? `Generate Diagram (${selectedIds.length} selected)`
          : `Generate Diagram (all ${appCount || ''})`.replace(' ()', '')}
    </Button>
  );

  return (
    <>
      <WorkspaceLayout
        panel={
          <OptionsPanel
            collapsed={panelCollapsed}
            onToggle={togglePanel}
            title="Intune Options"
            summary={panelSummary}
            footer={generateButton}
          >
            {optionsContent}
          </OptionsPanel>
        }
      >
        <DiagramPanel
          mermaidCode={mermaidCode}
          theme={formData.theme}
          layout={formData.layout}
          busy={loading}
          busyTitle={loading ? 'Generating diagram...' : undefined}
          onError={setError}
          onSuccess={setSuccess}
        />
      </WorkspaceLayout>

      {/* Collapsed panel hides inline alerts - surface them over the workspace. */}
      {panelCollapsed && (error || success) && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1300, maxWidth: 420 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </Box>
      )}

      <BlockSelectionDialog
        open={blockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
        apps={appList}
        onSelectBlock={handleBlockSelect}
        onSelectAll={handleRenderAll}
      />
    </>
  );
};

export default Online;
